import { ref } from 'vue';
import { driver, type DriveStep, type Config } from 'driver.js';
// CSS는 nuxt.config.ts에서 전역으로 로드됨

/**
 * 튜토리얼 관리 Composable
 * driver.js를 사용하여 사용자 가이드 튜토리얼을 제공합니다.
 */

// 튜토리얼 정의 타입
export interface TutorialStep {
  id: string;
  element: string; // CSS 선택자 또는 data-tutorial-id
  title?: string;
  description: string;
  onConfirm?: () => void | Promise<void>; // 확인 버튼 클릭 시 실행될 콜백
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export interface TutorialConfig {
  id: string;
  condition: () => boolean | Promise<boolean>; // 발동 조건
  beforeStart?: () => void | Promise<void>; // 튜토리얼 시작 전 실행 (페이지 이동 등)
  steps: TutorialStep[];
}

// 전역 상태: 대기 중인 튜토리얼
const pendingTutorial = ref<string | null>(null);
const registeredTutorials = ref<Map<string, TutorialConfig>>(new Map());

/** 현재 실행 중인 driver.js 인스턴스 (보드 추가 등에서 + 직접 클릭 시 튜토리얼 오버레이를 닫기 위해 사용) */
let activeTutorialDriver: ReturnType<typeof driver> | null = null;

// 강제 발동 설정 (테스트용)
// key: 튜토리얼 ID, value: true면 조건 무시하고 강제 발동
const forceTriggerMap = ref<Map<string, boolean>>(
  new Map([
    ['tutorial-board-add', false], // true로 변경하면 보드가 있어도 튜토리얼 발동
    ['tutorial-category-add', false], // true로 변경하면 카테고리가 있어도 튜토리얼 발동
  ]),
);

export function useTutorial() {
  /**
   * 실행 중인 튜토리얼(드라이버 오버레이)을 즉시 종료.
   * 예: 보드 없음 안내 튜토리얼이 떠 있는 상태에서 + 버튼으로 보드 등록 모달을 열 때.
   */
  const destroyActiveTutorial = () => {
    if (!activeTutorialDriver) return;
    try {
      activeTutorialDriver.destroy();
    } catch {
      // 이미 파괴된 경우 등 무시
    }
    activeTutorialDriver = null;
  };

  /**
   * 튜토리얼 등록
   */
  const registerTutorial = (config: TutorialConfig) => {
    registeredTutorials.value.set(config.id, config);
  };

  /**
   * 튜토리얼 발동 조건 체크 및 실행
   */
  const checkAndTriggerTutorial = async (tutorialId: string) => {
    const config = registeredTutorials.value.get(tutorialId);
    if (!config) {
      console.warn(`[Tutorial] Tutorial not found: ${tutorialId}`);
      return false;
    }

    // 발동 조건 체크
    const shouldTrigger = await config.condition();
    if (!shouldTrigger) {
      return false;
    }

    // beforeStart 실행 (페이지 이동 등)
    if (config.beforeStart) {
      pendingTutorial.value = tutorialId;
      await config.beforeStart();
      // beforeStart에서 페이지 이동이 있으면, 해당 페이지에서 executePendingTutorial 호출
      return true;
    }

    // 바로 실행
    await executeTutorial(config);
    return true;
  };

  /**
   * 대기 중인 튜토리얼 실행 (페이지 이동 후 호출)
   */
  const executePendingTutorial = async () => {
    if (!pendingTutorial.value) return;

    const tutorialId = pendingTutorial.value;
    const config = registeredTutorials.value.get(tutorialId);

    if (config) {
      // DOM이 준비될 때까지 약간 대기
      await new Promise(resolve => setTimeout(resolve, 500));
      await executeTutorial(config);
    }

    pendingTutorial.value = null;
  };

  /**
   * 튜토리얼 실행
   */
  const executeTutorial = async (config: TutorialConfig) => {
    destroyActiveTutorial();

    const steps: DriveStep[] = config.steps.map(step => ({
      element: step.element,
      popover: {
        title: step.title || '',
        description: step.description,
        side: step.position || 'bottom',
        align: 'center' as const,
      },
    }));

    const driverConfig: Config = {
      showProgress: false,
      showButtons: ['next'],
      nextBtnText: '확인',
      doneBtnText: '확인',
      popoverClass: 'tutorial-popover',
      steps,
      onPopoverRender: popover => {
        // 확인 버튼에 포커스
        setTimeout(() => {
          const nextBtn = popover.wrapper.querySelector(
            '.driver-popover-next-btn',
          ) as HTMLButtonElement;
          if (nextBtn) {
            nextBtn.focus();
          }
        }, 100);
      },
      onDestroyStarted: (_element, _step, opts) => {
        // 오버레이/ESC 등으로 종료 시작 시: driver.js 는 이 훅에서 driver.destroy() 를 호출해야 실제 정리가 진행됨
        activeTutorialDriver = null;
        opts.driver.destroy();
      },
      onNextClick: (element, step, opts) => {
        const currentStep = config.steps[opts.state.activeIndex || 0];
        if (currentStep?.onConfirm) {
          currentStep.onConfirm();
        }

        if (opts.state.activeIndex === config.steps.length - 1) {
          activeTutorialDriver = null;
          driverObj.destroy();
        } else {
          driverObj.moveNext();
        }
      },
    };

    const driverObj = driver(driverConfig);
    activeTutorialDriver = driverObj;
    driverObj.drive();
  };

  /**
   * 특정 튜토리얼 직접 실행
   */
  const startTutorial = async (tutorialId: string) => {
    const config = registeredTutorials.value.get(tutorialId);
    if (config) {
      await executeTutorial(config);
    }
  };

  /**
   * 대기 중인 튜토리얼 ID 가져오기
   */
  const getPendingTutorial = () => pendingTutorial.value;

  /**
   * 대기 중인 튜토리얼 설정
   */
  const setPendingTutorial = (tutorialId: string | null) => {
    pendingTutorial.value = tutorialId;
  };

  /**
   * 강제 발동 여부 설정 (테스트용)
   */
  const setForceTrigger = (tutorialId: string, force: boolean) => {
    forceTriggerMap.value.set(tutorialId, force);
    console.log(`[Tutorial] Force trigger for "${tutorialId}": ${force}`);
  };

  /**
   * 강제 발동 여부 조회
   */
  const isForceTrigger = (tutorialId: string): boolean => {
    return forceTriggerMap.value.get(tutorialId) || false;
  };

  /**
   * 전체 강제 발동 설정 조회
   */
  const getForceTriggerMap = () => forceTriggerMap.value;

  return {
    registerTutorial,
    checkAndTriggerTutorial,
    executePendingTutorial,
    startTutorial,
    destroyActiveTutorial,
    getPendingTutorial,
    setPendingTutorial,
    setForceTrigger,
    isForceTrigger,
    getForceTriggerMap,
  };
}
